// src/Components/InteractiveUSMap.tsx - MOBILE-OPTIMIZED VERSION with Region List
import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Row, Col, Card, CardBody, CardHeader, Button, ButtonGroup, Badge, Alert, ListGroup, ListGroupItem } from 'reactstrap';
import LoadingOverlay from './LoadingOverlay';

// Shared MapRegion interface
export interface MapRegion {
    region_name: string;
    raw_name?: string;
    state_code?: string;
    region_type?: 'state' | 'county' | 'city';
    job_count: number;
    avg_latitude?: number;
    avg_longitude?: number;
    state?: string;
}

interface MapData {
    level: 'country' | 'state';
    state?: string;
    regions: MapRegion[];
    summary: {
        total_regions: number;
        total_jobs: number;
        max_jobs_per_region: number;
        min_jobs_per_region: number;
        avg_jobs_per_region: number;
    };
    filters: {
        status?: string;
        categories?: string;
    };
}

interface InteractiveUSMapProps {
    level?: 'country' | 'state';
    state?: string;
    onRegionClick?: (region: MapRegion) => void;
    showFilters?: boolean;
    className?: string;
    height?: number;
}

const InteractiveUSMap: React.FC<InteractiveUSMapProps> = ({
    level = 'country',
    state,
    onRegionClick,
    showFilters = true,
    className = '',
    height = 600
}) => {
    const mapRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement | null>(null);

    const [mapData, setMapData] = useState<MapData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
    const [filters, setFilters] = useState({
        status: '',
        categories: ''
    });
    const [isMobile, setIsMobile] = useState(false);
    const [isTablet, setIsTablet] = useState(false);
    const [windowHeight, setWindowHeight] = useState(window.innerHeight);

    // NEW: Helper function to convert state codes to full names
    const getStateFullName = useCallback((regionName: string, stateCode?: string): string => {
        // If we're at country level, convert state codes to full names
        if (level === 'country') {
            const stateCodeToName: Record<string, string> = {
                'AL': 'Alabama', 'AK': 'Alaska', 'AZ': 'Arizona', 'AR': 'Arkansas', 'CA': 'California',
                'CO': 'Colorado', 'CT': 'Connecticut', 'DE': 'Delaware', 'FL': 'Florida', 'GA': 'Georgia',
                'HI': 'Hawaii', 'ID': 'Idaho', 'IL': 'Illinois', 'IN': 'Indiana', 'IA': 'Iowa',
                'KS': 'Kansas', 'KY': 'Kentucky', 'LA': 'Louisiana', 'ME': 'Maine', 'MD': 'Maryland',
                'MA': 'Massachusetts', 'MI': 'Michigan', 'MN': 'Minnesota', 'MS': 'Mississippi', 'MO': 'Missouri',
                'MT': 'Montana', 'NE': 'Nebraska', 'NV': 'Nevada', 'NH': 'New Hampshire', 'NJ': 'New Jersey',
                'NM': 'New Mexico', 'NY': 'New York', 'NC': 'North Carolina', 'ND': 'North Dakota', 'OH': 'Ohio',
                'OK': 'Oklahoma', 'OR': 'Oregon', 'PA': 'Pennsylvania', 'RI': 'Rhode Island', 'SC': 'South Carolina',
                'SD': 'South Dakota', 'TN': 'Tennessee', 'TX': 'Texas', 'UT': 'Utah', 'VT': 'Vermont',
                'VA': 'Virginia', 'WA': 'Washington', 'WV': 'West Virginia', 'WI': 'Wisconsin', 'WY': 'Wyoming',
                'DC': 'District of Columbia'
            };
            
            // Try to convert region_name if it looks like a state code
            if (regionName.length === 2) {
                return stateCodeToName[regionName.toUpperCase()] || regionName;
            }
            
            // Try to convert state_code if available
            if (stateCode && stateCode.length === 2) {
                return stateCodeToName[stateCode.toUpperCase()] || regionName;
            }
        }
        
        // For counties or if no conversion found, return the original region name
        return regionName;
    }, [level]);

    // NEW: Memoized sorted regions for alphabetical list with proper display names
    const sortedRegions = useMemo(() => {
        if (!mapData?.regions) return [];
        return [...mapData.regions]
            .map(region => ({
                ...region,
                displayName: getStateFullName(region.region_name, region.state_code)
            }))
            .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }, [mapData?.regions, getStateFullName]);

    // MOBILE-OPTIMIZED: Dynamic height calculation
    const getOptimalHeight = useCallback(() => {
        if (isMobile) {
            // Mobile: Use most of the viewport height minus header/footer space
            return Math.min(windowHeight * 0.6, 400); // 60% of viewport or 400px max
        } else if (isTablet) {
            // Tablet: Slightly taller
            return Math.min(windowHeight * 0.7, 500);
        } else {
            // Desktop: Use provided height or default
            return height;
        }
    }, [isMobile, isTablet, windowHeight, height]);

    const getRegionColor = useCallback((jobCount: number, maxJobs: number): string => {
        if (jobCount === 0) return '#f8f9fa'; // Light gray for 0 jobs

        const intensity = jobCount / Math.max(maxJobs, 1); // Prevent division by 0
        if (intensity <= 0.25) return '#C0D9E9';
        if (intensity <= 0.5) return '#85c1e9';
        if (intensity <= 0.75) return '#3498db';
        return '#1f4e79';
    }, []);

    // Helper function to get state name from TopoJSON properties
    const getStateName = useCallback((properties: any): string => {
        const name = properties.name || properties.NAME || properties.STATE_NAME || properties.STUSPS;
        return name || 'Unknown';
    }, []);

    // Helper function to get county name from TopoJSON properties
    const getCountyName = useCallback((properties: any): string => {
        return properties.name || properties.NAME || properties.NAMELSAD || properties.COUNTY || 'Unknown County';
    }, []);

    // Fixed state matching function
    const findRegionData = useCallback((stateProperties: any) => {
        const stateName = getStateName(stateProperties);
        
        const stateNameToCode: Record<string, string> = {
            'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
            'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
            'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
            'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
            'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
            'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
            'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
            'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
            'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
            'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY',
            'District of Columbia': 'DC'
        };

        const stateCode = stateNameToCode[stateName];
        if (!stateCode) return null;

        const regionData = mapData?.regions.find(r =>
            r.state_code?.toUpperCase() === stateCode.toUpperCase()
        );

        return regionData;
    }, [mapData, getStateName]);

    const findCountyData = useCallback((countyProperties: any) => {
        if (!mapData || !mapData.regions) return null;

        const countyName = getCountyName(countyProperties);
        
        const countyLookup: Record<string, number> = {};
        mapData.regions.forEach(region => {
            const cleanName = region.region_name.replace(/ County$/i, '').toUpperCase();
            countyLookup[cleanName] = region.job_count;
            countyLookup[region.region_name.toUpperCase()] = region.job_count;

            if (region.raw_name) {
                const cleanRawName = region.raw_name.replace(/ County$/i, '').toUpperCase();
                countyLookup[cleanRawName] = region.job_count;
                countyLookup[region.raw_name.toUpperCase()] = region.job_count;
            }
        });

        const upperCountyName = countyName.toUpperCase();
        if (countyLookup[upperCountyName] !== undefined) {
            return {
                region_name: countyName,
                job_count: countyLookup[upperCountyName],
                raw_name: countyName,
                region_type: 'county' as const
            };
        }

        return {
            region_name: countyName,
            job_count: 0,
            raw_name: countyName,
            region_type: 'county' as const
        };
    }, [mapData, getCountyName]);

    // Get state FIPS code for filtering counties
    const getStateFips = useCallback((stateCode: string): string | null => {
        const stateFips: Record<string, string> = {
            'AL': '01', 'AK': '02', 'AZ': '04', 'AR': '05', 'CA': '06', 'CO': '08', 'CT': '09', 'DE': '10',
            'FL': '12', 'GA': '13', 'HI': '15', 'ID': '16', 'IL': '17', 'IN': '18', 'IA': '19', 'KS': '20',
            'KY': '21', 'LA': '22', 'ME': '23', 'MD': '24', 'MA': '25', 'MI': '26', 'MN': '27', 'MS': '28',
            'MO': '29', 'MT': '30', 'NE': '31', 'NV': '32', 'NH': '33', 'NJ': '34', 'NM': '35', 'NY': '36',
            'NC': '37', 'ND': '38', 'OH': '39', 'OK': '40', 'OR': '41', 'PA': '42', 'RI': '44', 'SC': '45',
            'SD': '46', 'TN': '47', 'TX': '48', 'UT': '49', 'VT': '50', 'VA': '51', 'WA': '53', 'WV': '54',
            'WI': '55', 'WY': '56', 'DC': '11'
        };
        return stateFips[stateCode.toUpperCase()] || null;
    }, []);

    // NEW: Handle region click from list
    const handleRegionClick = useCallback((region: MapRegion) => {
        setSelectedRegion(region.region_name);
        if (onRegionClick) {
            onRegionClick(region);
        }
    }, [onRegionClick]);

    // Fetch map data from backend - RESTORED ORIGINAL
    const fetchMapData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams({
                level,
                ...(state && { state }),
                ...(filters.status && { status: filters.status }),
                ...(filters.categories && { categories: filters.categories })
            });

            const baseURL = process.env.REACT_APP_SERVER || "https://www.bidbro.com/api";
            const url = `${baseURL}/jobs/map-data?${params}`;

            const response = await fetch(url);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const data: MapData = await response.json();
            setMapData(data);

        } catch (err) {
            console.error('❌ Frontend: Error fetching map data:', err);
            setError(err instanceof Error ? err.message : 'Failed to load map data');
        } finally {
            setLoading(false);
        }
    }, [level, state, filters.status, filters.categories]);

    // MOBILE-OPTIMIZED: Device detection with resize handler
    useEffect(() => {
        const handleResize = () => {
            const width = window.innerWidth;
            setIsMobile(width < 768);
            setIsTablet(width >= 768 && width < 1024);
            setWindowHeight(window.innerHeight);
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Load map data on mount and when dependencies change
    useEffect(() => {
        fetchMapData();
    }, [fetchMapData]);

    // Initialize D3 map visualization with mobile optimizations
    useEffect(() => {
        if (!mapData || !mapRef.current || loading) return;

        const initializeMap = async () => {
            try {
                await loadD3FromCDN();
            } catch (error) {
                console.error('Error initializing map:', error);
                drawSimplifiedVisualization();
            }
        };

        const loadD3FromCDN = (): Promise<void> => {
            return new Promise((resolve, reject) => {
                if ((window as any).d3) {
                    initializeMapWithD3((window as any).d3);
                    resolve();
                    return;
                }

                const d3Script = document.createElement('script');
                d3Script.src = 'https://cdnjs.cloudflare.com/ajax/libs/d3/7.8.5/d3.min.js';
                d3Script.onload = () => {
                    const topoScript = document.createElement('script');
                    topoScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/topojson/3.0.2/topojson.min.js';
                    topoScript.onload = () => {
                        initializeMapWithD3((window as any).d3);
                        resolve();
                    };
                    topoScript.onerror = () => {
                        console.warn('TopoJSON failed to load, using D3 only');
                        initializeMapWithD3((window as any).d3);
                        resolve();
                    };
                    document.head.appendChild(topoScript);
                };
                d3Script.onerror = () => {
                    console.error('Failed to load D3 from CDN');
                    drawSimplifiedVisualization();
                    reject(new Error('Failed to load D3'));
                };
                document.head.appendChild(d3Script);
            });
        };

        const initializeMapWithD3 = async (d3: any) => {
            const container = mapRef.current!;
            const containerRect = container.getBoundingClientRect();
            const width = containerRect.width;
            const mapHeight = getOptimalHeight(); // MOBILE-OPTIMIZED: Use dynamic height

            d3.select(container).selectAll('svg').remove();

            const svg = d3.select(container)
                .append('svg')
                .attr('width', width)
                .attr('height', mapHeight)
                .style('background-color', '#ffffff');

            svgRef.current = svg.node();

            if (level === 'country') {
                try {
                    const topojson = (window as any).topojson;

                    if (!topojson) {
                        console.warn('TopoJSON not available, using simplified visualization');
                        drawSimplifiedStates(svg, width, mapHeight, d3);
                        return;
                    }

                    const usResponse = await fetch('https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json');

                    if (!usResponse.ok) throw new Error('Failed to load map data');

                    const us = await usResponse.json();
                    const states = topojson.feature(us, us.objects.states);

                    // MOBILE-OPTIMIZED: Adjust projection scale based on device
                    const getProjectionScale = () => {
                        if (isMobile) return width * 0.8; // Larger scale for mobile
                        if (isTablet) return width * 0.75;
                        return width * 0.7;
                    };

                    const projection = d3.geoAlbersUsa()
                        .scale(getProjectionScale())
                        .translate([width / 2, mapHeight / 2]);

                    const path = d3.geoPath().projection(projection);

                    // MOBILE-OPTIMIZED: Create responsive tooltip
                    const createTooltip = () => {
                        return d3.select('body').selectAll('.map-tooltip')
                            .data([0])
                            .enter()
                            .append('div')
                            .attr('class', 'map-tooltip')
                            .style('position', 'absolute')
                            .style('visibility', 'hidden')
                            .style('background-color', 'rgba(0, 0, 0, 0.9)')
                            .style('color', 'white')
                            .style('padding', isMobile ? '8px 12px' : '12px 16px')
                            .style('border-radius', '8px')
                            .style('font-size', isMobile ? '12px' : '14px')
                            .style('z-index', '1000')
                            .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)')
                            .style('pointer-events', 'none');
                    };

                    let tooltip = d3.select('body').select('.map-tooltip');
                    if (tooltip.empty()) {
                        tooltip = createTooltip();
                    }

                    // MOBILE-OPTIMIZED: Responsive stroke width
                    const getStrokeWidth = () => {
                        if (isMobile) return 0.5;
                        if (isTablet) return 1;
                        return 1.5;
                    };

                    svg.selectAll('.state')
                        .data(states.features)
                        .enter()
                        .append('path')
                        .attr('class', 'state')
                        .attr('d', path)
                        .style('fill', (d: any) => {
                            const regionData = findRegionData(d.properties);
                            const color = regionData ? 
                                getRegionColor(regionData.job_count, Math.max(mapData.summary.max_jobs_per_region, 1)) : '#f0f0f0';
                            return color;
                        })
                        .style('stroke', '#ffffff')
                        .style('stroke-width', getStrokeWidth())
                        .style('cursor', 'pointer')
                        .on('mouseover', function (this: any, event: any, d: any) {
                            const regionData = findRegionData(d.properties);
                            const stateName = getStateName(d.properties);

                            d3.select(this).style('stroke-width', getStrokeWidth() * 2);

                            tooltip
                                .html(`
                                    <strong>${stateName}</strong><br/>
                                    Jobs: ${regionData?.job_count || 0}
                                `)
                                .style('visibility', 'visible');
                        })
                        .on('mousemove', function (event: any) {
                            const offset = isMobile ? 10 : 20;
                            tooltip
                                .style('top', (event.pageY - 60) + 'px')
                                .style('left', (event.pageX + offset) + 'px');
                        })
                        .on('mouseout', function (this: any) {
                            d3.select(this).style('stroke-width', getStrokeWidth());
                            tooltip.style('visibility', 'hidden');
                        })
                        .on('click', function (this: any, event: any, d: any) {
                            const regionData = findRegionData(d.properties);
                            if (regionData && onRegionClick) {
                                setSelectedRegion(regionData.region_name);
                                onRegionClick(regionData);
                            }
                        });

                } catch (error) {
                    console.error('Error drawing states map:', error);
                    drawSimplifiedStates(svg, width, mapHeight, d3);
                }
            } else {
                await drawStateCounties(svg, width, mapHeight, d3);
            }
        };

        const drawStateCounties = async (svg: any, width: number, height: number, d3: any) => {
            if (!mapData || !state) return;

            try {
                const topojson = (window as any).topojson;

                if (!topojson) {
                    console.warn('TopoJSON not available, using simplified visualization');
                    drawSimplifiedCounties(svg, width, height, d3);
                    return;
                }

                const countiesResponse = await fetch('https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json');

                if (!countiesResponse.ok) throw new Error('Failed to load county data');

                const us = await countiesResponse.json();
                const usCounties = topojson.feature(us, us.objects.counties);

                const stateFips = getStateFips(state);

                if (!stateFips) {
                    drawSimplifiedCounties(svg, width, height, d3);
                    return;
                }

                const stateCounties = usCounties.features.filter((d: any) =>
                    d.id.toString().substring(0, 2) === stateFips
                );

                if (stateCounties.length === 0) {
                    console.warn('No counties found for state:', state);
                    drawSimplifiedCounties(svg, width, height, d3);
                    return;
                }

                // MOBILE-OPTIMIZED: Adjust projection for counties
                const getCountyProjectionScale = () => {
                    if (isMobile) return width * 1.2;
                    if (isTablet) return width * 1.1;
                    return width;
                };

                const projection = d3.geoAlbersUsa()
                    .scale(getCountyProjectionScale())
                    .translate([width / 2, height / 2]);

                const path = d3.geoPath().projection(projection);

                svg.selectAll('*').remove();
                const g = svg.append('g');

                let tooltip = d3.select('body').select('.map-tooltip');
                if (tooltip.empty()) {
                    tooltip = d3.select('body')
                        .append('div')
                        .attr('class', 'map-tooltip')
                        .style('position', 'absolute')
                        .style('visibility', 'hidden')
                        .style('background-color', 'rgba(0, 0, 0, 0.9)')
                        .style('color', 'white')
                        .style('padding', isMobile ? '8px 12px' : '12px 16px')
                        .style('border-radius', '8px')
                        .style('font-size', isMobile ? '12px' : '14px')
                        .style('z-index', '1000')
                        .style('box-shadow', '0 4px 12px rgba(0,0,0,0.3)')
                        .style('pointer-events', 'none');
                }

                // MOBILE-OPTIMIZED: Ultra-thin strokes for mobile counties
                const getCountyStrokeWidth = () => {
                    if (isMobile) return 0.1;
                    if (isTablet) return 0.3;
                    return 0.5;
                };

                g.selectAll('.county')
                    .data(stateCounties)
                    .enter()
                    .append('path')
                    .attr('class', 'county')
                    .attr('d', path)
                    .style('fill', (d: any) => {
                        const regionData = findCountyData(d.properties);
                        const color = regionData ? 
                            getRegionColor(regionData.job_count, Math.max(mapData.summary.max_jobs_per_region, 1)) : '#f0f0f0';
                        return color;
                    })
                    .style('stroke', '#ffffff')
                    .style('stroke-width', getCountyStrokeWidth())
                    .style('cursor', 'pointer')
                    .on('mouseover', function (this: any, event: any, d: any) {
                        const regionData = findCountyData(d.properties);
                        const countyName = getCountyName(d.properties);

                        d3.select(this)
                            .style('stroke-width', getCountyStrokeWidth() * 3)
                            .style('stroke', '#2c3e50');

                        tooltip
                            .html(`
                                <strong>${countyName}</strong><br/>
                                Jobs: ${regionData?.job_count || 0}
                            `)
                            .style('visibility', 'visible');
                    })
                    .on('mousemove', function (event: any) {
                        const offset = isMobile ? 10 : 20;
                        tooltip
                            .style('top', (event.pageY - 60) + 'px')
                            .style('left', (event.pageX + offset) + 'px');
                    })
                    .on('mouseout', function (this: any) {
                        d3.select(this)
                            .style('stroke-width', getCountyStrokeWidth())
                            .style('stroke', '#ffffff');
                        tooltip.style('visibility', 'hidden');
                    })
                    .on('click', function (this: any, event: any, d: any) {
                        const regionData = findCountyData(d.properties);
                        if (regionData && onRegionClick) {
                            setSelectedRegion(regionData.region_name);
                            onRegionClick(regionData);
                        }
                    });

                zoomToState(state, g, path, svg, d3, stateCounties, width, height);

            } catch (error) {
                drawSimplifiedCounties(svg, width, height, d3);
            }
        };

        const zoomToState = (stateCode: string, g: any, path: any, svg: any, d3: any, stateCounties: any[], width: number, height: number) => {
            if (!stateCounties || stateCounties.length === 0) return;

            const bounds = path.bounds({
                type: "FeatureCollection",
                features: stateCounties
            });

            const dx = bounds[1][0] - bounds[0][0];
            const dy = bounds[1][1] - bounds[0][1];
            const x = (bounds[0][0] + bounds[1][0]) / 2;
            const y = (bounds[0][1] + bounds[1][1]) / 2;
            
            // MOBILE-OPTIMIZED: Adjust zoom scale
            const getZoomScale = () => {
                const baseScale = Math.min(8, 0.9 / Math.max(dx / width, dy / height));
                if (isMobile) return baseScale * 1.2; // Zoom in more on mobile
                return baseScale;
            };
            
            const scale = getZoomScale();
            const translate = [width / 2 - scale * x, height / 2 - scale * y];

            g.transition()
                .duration(750)
                .attr('transform', `translate(${translate}) scale(${scale})`);
        };

        // MOBILE-OPTIMIZED: Simplified states with better mobile layout
        const drawSimplifiedStates = async (svg: any, width: number, height: number, d3: any) => {
            if (!mapData) return;

            // MOBILE-OPTIMIZED: Responsive grid layout
            const getGridLayout = () => {
                if (isMobile) return { cols: 6, rows: 9 }; // Taller, narrower grid for mobile
                if (isTablet) return { cols: 8, rows: 7 };
                return { cols: 10, rows: 5 }; // Original desktop layout
            };

            const { cols, rows } = getGridLayout();
            const cellWidth = width / cols;
            const cellHeight = height / rows;

            svg.selectAll('.state-rect')
                .data(mapData.regions.slice(0, 50))
                .enter()
                .append('rect')
                .attr('class', 'state-rect')
                .attr('x', (_: any, i: number) => (i % cols) * cellWidth + (isMobile ? 2 : 10))
                .attr('y', (_: any, i: number) => Math.floor(i / cols) * cellHeight + (isMobile ? 2 : 10))
                .attr('width', cellWidth - (isMobile ? 4 : 20))
                .attr('height', cellHeight - (isMobile ? 4 : 20))
                .attr('rx', isMobile ? 4 : 8)
                .style('fill', (d: MapRegion) => getRegionColor(d.job_count, mapData.summary.max_jobs_per_region))
                .style('stroke', '#ffffff')
                .style('stroke-width', isMobile ? 1 : 2)
                .style('cursor', 'pointer')
                .on('click', function (this: any, event: any, d: MapRegion) {
                    setSelectedRegion(d.region_name);
                    if (onRegionClick) {
                        onRegionClick(d);
                    }
                });

            // MOBILE-OPTIMIZED: Responsive font size for labels
            const getFontSize = () => {
                if (isMobile) return '8px';
                if (isTablet) return '10px';
                return '12px';
            };

            svg.selectAll('.state-label')
                .data(mapData.regions.slice(0, 50))
                .enter()
                .append('text')
                .attr('class', 'state-label')
                .attr('x', (_: any, i: number) => (i % cols) * cellWidth + cellWidth / 2)
                .attr('y', (_: any, i: number) => Math.floor(i / cols) * cellHeight + cellHeight / 2)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .style('font-size', getFontSize())
                .style('font-weight', 'bold')
                .style('fill', '#2c3e50')
                .text((d: MapRegion) => d.state_code || d.region_name.substring(0, 2).toUpperCase());
        };

        // MOBILE-OPTIMIZED: Simplified counties with responsive layout
        const drawSimplifiedCounties = (svg: any, width: number, height: number, d3: any) => {
            if (!mapData) return;

            const padding = isMobile ? 10 : 40;
            const innerWidth = width - 2 * padding;
            const innerHeight = height - 2 * padding;

            // MOBILE-OPTIMIZED: Responsive grid for counties
            const getCountyGrid = () => {
                const totalCounties = mapData.regions.length;
                if (isMobile) {
                    // Mobile: Fewer columns, more rows
                    const cols = Math.min(3, Math.ceil(Math.sqrt(totalCounties * 0.5)));
                    return { cols, rows: Math.ceil(totalCounties / cols) };
                } else if (isTablet) {
                    const cols = Math.ceil(Math.sqrt(totalCounties * 0.7));
                    return { cols, rows: Math.ceil(totalCounties / cols) };
                }
                const cols = Math.ceil(Math.sqrt(totalCounties));
                return { cols, rows: Math.ceil(totalCounties / cols) };
            };

            const { cols } = getCountyGrid();
            const cellWidth = innerWidth / cols;
            const cellHeight = innerHeight / Math.ceil(mapData.regions.length / cols);

            svg.selectAll('.county-rect')
                .data(mapData.regions)
                .enter()
                .append('rect')
                .attr('class', 'county-rect')
                .attr('x', (_: any, i: number) => padding + (i % cols) * cellWidth + (isMobile ? 2 : 10))
                .attr('y', (_: any, i: number) => padding + Math.floor(i / cols) * cellHeight + (isMobile ? 2 : 10))
                .attr('width', cellWidth - (isMobile ? 4 : 20))
                .attr('height', cellHeight - (isMobile ? 4 : 20))
                .attr('rx', isMobile ? 4 : 8)
                .style('fill', (d: MapRegion) => getRegionColor(d.job_count, mapData.summary.max_jobs_per_region))
                .style('stroke', '#ffffff')
                .style('stroke-width', isMobile ? 1 : 2)
                .style('cursor', 'pointer')
                .on('click', function (this: any, event: any, d: MapRegion) {
                    setSelectedRegion(d.region_name);
                    if (onRegionClick) {
                        onRegionClick(d);
                    }
                });

            // Add county labels with responsive font size
            svg.selectAll('.county-label')
                .data(mapData.regions)
                .enter()
                .append('text')
                .attr('class', 'county-label')
                .attr('x', (_: any, i: number) => padding + (i % cols) * cellWidth + cellWidth / 2)
                .attr('y', (_: any, i: number) => padding + Math.floor(i / cols) * cellHeight + cellHeight / 2)
                .attr('text-anchor', 'middle')
                .attr('dominant-baseline', 'middle')
                .style('font-size', isMobile ? '6px' : '10px')
                .style('font-weight', 'bold')
                .style('fill', '#2c3e50')
                .text((d: MapRegion) => {
                    const name = d.raw_name || d.region_name;
                    const maxLength = isMobile ? 8 : 12;
                    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
                });
        };

        // MOBILE-OPTIMIZED: Fallback visualization with responsive grid
        const drawSimplifiedVisualization = () => {
            if (!mapRef.current || !mapData) return;

            const getGridColumns = () => {
                if (isMobile) return 'repeat(auto-fit, minmax(120px, 1fr))'; // Smaller cards on mobile
                if (isTablet) return 'repeat(auto-fit, minmax(160px, 1fr))';
                return 'repeat(auto-fit, minmax(200px, 1fr))';
            };

            const currentHeight = getOptimalHeight();

            mapRef.current.innerHTML = `
                <div style="
                    display: grid;
                    grid-template-columns: ${getGridColumns()};
                    gap: ${isMobile ? '8px' : '16px'};
                    padding: ${isMobile ? '10px' : '20px'};
                    max-height: ${currentHeight}px;
                    overflow-y: auto;
                    -webkit-overflow-scrolling: touch;
                ">
                    ${mapData.regions.map(region => `
                        <div style="
                            padding: ${isMobile ? '8px' : '16px'};
                            border-radius: ${isMobile ? '6px' : '8px'};
                            border: 2px solid ${getRegionColor(region.job_count, mapData.summary.max_jobs_per_region)};
                            background-color: ${region.region_name === selectedRegion ? '#e3f2fd' : '#ffffff'};
                            cursor: pointer;
                            text-align: center;
                            color: ${region.job_count === 0 ? '#666' : '#2c3e50'};
                            font-weight: bold;
                            transition: transform 0.2s ease;
                            font-size: ${isMobile ? '12px' : '16px'};
                        "
                        onclick="window.dispatchEvent(new CustomEvent('regionClick', { detail: ${JSON.stringify(region).replace(/"/g, '&quot;')} }))"
                        onmouseover="this.style.transform='scale(1.05)'"
                        onmouseout="this.style.transform='scale(1)'"
                        >
                            <div style="font-size: ${isMobile ? '12px' : '16px'}; margin-bottom: ${isMobile ? '4px' : '8px'};">
                                ${region.region_name.length > (isMobile ? 12 : 20) ? 
                                    region.region_name.substring(0, isMobile ? 12 : 20) + '...' : 
                                    region.region_name}
                            </div>
                            <div style="font-size: ${isMobile ? '10px' : '14px'}; opacity: 0.9;">${region.job_count} jobs</div>
                        </div>
                    `).join('')}
                </div>
            `;

            // Add event listener for region clicks
            const handleRegionClick = (event: any) => {
                const region = event.detail;
                setSelectedRegion(region.region_name);
                if (onRegionClick) {
                    onRegionClick(region);
                }
            };

            window.addEventListener('regionClick', handleRegionClick);

            return () => {
                window.removeEventListener('regionClick', handleRegionClick);
            };
        };

        initializeMap();
    }, [mapData, level, state, getOptimalHeight, getRegionColor, onRegionClick, selectedRegion, findRegionData, findCountyData, getStateName, getCountyName, getStateFips, isMobile, isTablet]);

    // Clean up tooltips on unmount
    useEffect(() => {
        return () => {
            const tooltips = document.querySelectorAll('.map-tooltip');
            tooltips.forEach(tooltip => tooltip.remove());
        };
    }, []);

    if (loading) {
        return (
            <div className={className}>
                <LoadingOverlay isVisible={true} />
                <div style={{ height: getOptimalHeight(), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div>Loading map data...</div>
                </div>
            </div>
        );
    }

    if (error || !mapData) {
        return (
            <div className={className}>
                <Alert color="danger">
                    <h6>Error Loading Map</h6>
                    <p className="mb-0">{error || 'Map data is not available'}</p>
                </Alert>
            </div>
        );
    }

    const currentHeight = getOptimalHeight();

    return (
        <div className={className}>
            {/* Main content area with responsive layout */}
            <Row className={`${isMobile || isTablet ? 'mb-0' : 'mb-0'}`}>
                {/* Map Column - Full width on mobile/tablet, 8 cols on desktop */}
                <Col xs={12} sm={12} md={12} lg={9} xl={10}>
                    <Card>
                        <CardHeader className={`d-flex justify-content-between align-items-center ${isMobile ? 'py-2' : ''}`}>
                            <div>
                                <h5 className={`card-title mb-1 ${isMobile ? 'fs-6' : ''}`}>
                                    {level === 'country' ? 'Jobs by State' : `Jobs in ${state?.toUpperCase() || 'State'} Counties`}
                                </h5>
                                <small className="text-muted">
                                    {mapData.summary.total_regions} {level === 'country' ? 'states' : 'regions'} • {mapData.summary.total_jobs.toLocaleString()} total jobs
                                </small>
                            </div>
                        </CardHeader>

                        <CardBody className={`${isMobile || isTablet ? 'p-2' : 'pb-4 pt-4'}`}>
                            <div 
                                ref={mapRef} 
                                style={{ 
                                    width: '100%', 
                                    height: currentHeight,
                                    // MOBILE-OPTIMIZED: Better touch scrolling
                                    overflowY: 'auto',
                                    WebkitOverflowScrolling: 'touch'
                                }} 
                            />
                        </CardBody>
                    </Card>
                </Col>

                {/* NEW: Region List Column - Right side on desktop, below on mobile/tablet */}
                <Col xs={12} sm={12} md={12} lg={3} xl={2}>
                    <Card>
                        <CardHeader className={`${isMobile ? 'py-2' : ''}`}>
                            <h6 className={`card-title mb-0 ${isMobile ? 'fs-6' : ''}`}>
                                Project Counts by {level === 'country' ? 'State' : 'County'}
                            </h6>
                        </CardHeader>
                        <CardBody className={`${isMobile || isTablet ? 'p-2' : ''}`} style={{ maxHeight: currentHeight, overflow: 'auto' }}>
                            <ListGroup flush>
                                {sortedRegions.map((region, index) => (
                                    <ListGroupItem
                                        key={index}
                                        action
                                        active={selectedRegion === region.region_name}
                                        onClick={() => handleRegionClick(region)}
                                        className={`d-flex justify-content-between align-items-center ${isMobile ? 'py-2 px-2' : 'py-2 px-3'}`}
                                        style={{ 
                                            cursor: 'pointer',
                                            fontSize: isMobile ? '0.875rem' : '0.9rem'
                                        }}
                                    >
                                        <span className="text-truncate me-2" style={{ maxWidth: '70%' }}>
                                            {(region as any).displayName}
                                        </span>
                                        <Badge 
                                            color={region.job_count > 0 ? 'primary' : 'light'} 
                                            pill
                                            className={isMobile ? 'px-2 py-1' : ''}
                                        >
                                            <small className={isMobile ? 'fs-7' : ''}>
                                                {region.job_count.toLocaleString()}
                                            </small>
                                        </Badge>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        </CardBody>
                    </Card>
                </Col>
            </Row>

            {/* MOBILE-OPTIMIZED: Responsive legend */}
            <Row>
                <Col>
                    <Card className="bg-light">
                        <CardBody className={`${isMobile || isTablet ? 'py-2' : 'py-3'}`}>
                            <div className={`d-flex ${isMobile ? 'flex-column gap-2' : 'justify-content-between'} align-items-${isMobile ? 'start' : 'center'}`}>
                                <div className={`d-flex align-items-center ${isMobile ? 'flex-wrap' : ''} gap-${isMobile ? '2' : '3'}`}>
                                    <small className={`fw-semibold text-muted ${isMobile ? 'fs-7' : ''}`}>
                                        Jobs per {level === 'country' ? 'state' : 'region'}:
                                    </small>
                                    <div className={`d-flex align-items-center ${isMobile ? 'flex-wrap' : ''} gap-${isMobile ? '1' : '2'}`}>
                                        <div className="d-flex align-items-center gap-1">
                                            <div style={{ 
                                                width: isMobile ? 10 : 16, 
                                                height: isMobile ? 10 : 16, 
                                                backgroundColor: '#C0D9E9',
                                                borderRadius: '2px'
                                            }}></div>
                                            <small className={isMobile ? 'fs-7' : ''}>Low</small>
                                        </div>
                                        <div className="d-flex align-items-center gap-1">
                                            <div style={{ 
                                                width: isMobile ? 10 : 16, 
                                                height: isMobile ? 10 : 16, 
                                                backgroundColor: '#3498db',
                                                borderRadius: '2px'
                                            }}></div>
                                            <small className={isMobile ? 'fs-7' : ''}>High</small>
                                        </div>
                                    </div>
                                </div>
                                <div className={`d-flex align-items-center ${isMobile ? 'flex-wrap' : ''} gap-${isMobile ? '2' : '3'}`}>
                                    <Badge color="primary" pill className={isMobile ? 'px-2 py-1' : ''}>
                                        <small className={isMobile ? 'fs-7' : ''}>
                                            Avg: {Math.round(mapData.summary.avg_jobs_per_region)} jobs
                                        </small>
                                    </Badge>
                                    <Badge color="success" pill className={isMobile ? 'px-2 py-1' : ''}>
                                        <small className={isMobile ? 'fs-7' : ''}>
                                            Max: {mapData.summary.max_jobs_per_region} jobs
                                        </small>
                                    </Badge>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default InteractiveUSMap;